import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { AudioButton } from '../components/AudioButton';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import axios from 'axios';
import {
  ChevronLeft,
  BookOpen,
  Play,
  Check,
  Lock,
  Star,
  Volume2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Stories() {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [completedStories, setCompletedStories] = useState([]);

  useEffect(() => {
    fetchStories();
  }, []);

  const fetchStories = async () => {
    try {
      const response = await axios.get(`${API}/stories`, { withCredentials: true });
      // API returns { stories: [...], total_stories: ..., by_level: {...} }
      setStories(response.data.stories || response.data || []);
    } catch (error) {
      toast.error('Failed to load stories');
    } finally {
      setLoading(false);
    }
  };

  const getTitle = (story) => {
    if (language === 'de') return story.title_de;
    if (language === 'ar') return story.title_ar;
    return story.title_en;
  };

  const levelColors = {
    A1: 'bg-teal-500',
    A2: 'bg-blue-500',
    B1: 'bg-purple-500',
    B2: 'bg-amber-500'
  };

  const groupedStories = stories.reduce((acc, story) => {
    if (!acc[story.level]) acc[story.level] = [];
    acc[story.level].push(story);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-background" data-testid="stories-page">
      {/* Header */}
      <header className="sticky top-0 bg-background/80 backdrop-blur-md border-b border-border z-40">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate('/dashboard')}
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="font-heading text-xl font-bold">Stories</h1>
              <p className="text-muted-foreground text-sm">Interactive German stories</p>
            </div>
          </div>
          <Badge variant="outline">
            <BookOpen className="h-4 w-4 mr-1" />
            {stories.length} stories
          </Badge>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {loading ? (
          <div className="space-y-6">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={`skel-${i}`} className="h-32 rounded-xl" />
            ))}
          </div>
        ) : stories.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <BookOpen className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground mb-4">No stories available yet.</p>
              <Button onClick={() => navigate('/lessons')}>Go to Lessons</Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-8">
            {Object.entries(groupedStories).map(([level, levelStories]) => (
              <div key={level}>
                <div className="flex items-center gap-2 mb-4">
                  <Badge className={levelColors[level]}>{level}</Badge>
                  <span className="text-muted-foreground text-sm">
                    {levelStories.length} {levelStories.length === 1 ? 'story' : 'stories'}
                  </span>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  {levelStories.map((story) => {
                    const isCompleted = completedStories.includes(story.story_id);
                    return (
                      <Card 
                        key={story.story_id}
                        className={`overflow-hidden transition-all hover:shadow-lg cursor-pointer ${
                          isCompleted ? 'opacity-75' : ''
                        }`}
                        onClick={() => navigate(`/story/${story.story_id}`)}
                        data-testid={`story-${story.story_id}`}
                      >
                        <div className="h-2 bg-gradient-to-r from-teal-500 to-amber-500" />
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                {isCompleted && (
                                  <Check className="h-4 w-4 text-green-500" />
                                )}
                                <h3 className="font-semibold">{getTitle(story)}</h3>
                                <AudioButton text={story.title_de} />
                              </div>
                              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <Star className="h-4 w-4 text-amber-500" />
                                  {story.xp_reward} XP
                                </span>
                                <span>{story.scenes?.length || 0} scenes</span>
                              </div>
                            </div>
                            <Button size="sm" className="bg-teal-500 hover:bg-teal-600">
                              <Play className="h-4 w-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
